// @flow
import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';

import {useOutsideAlerter} from '../../utils/dom';
import {IconButton, Typography, makeStyles} from '@material-ui/core';

import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import {colors} from '../../assets/styles/colors';

type Props = {
  label?: string,
  onFocus?: (val: any) => void,
  availableOptions: string[],
  selectedOptions: string[],
  setOptions: (options: string[]) => void,
  minSelected?: number,
  maxSelected?: number,
};

const useStyles = makeStyles(theme => ({
  btn: {
    background: 'none',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    textDecoration: 'none!important',
    outline: 'none',
    border: 'none',
    borderBottom: `1px solid ${colors.grey200}`,
    cursor: 'pointer',
  },
  header: {
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  wrap: {
    border: '1.25px solid ' + theme.palette.grey[300],
    padding: theme.spacing(1),
    borderRadius: 10,
  },
  optionsWrap: {
    width: '100%',
    backgroundColor: theme.palette.common.white,
    zIndex: 999999,
    display: 'flex',
    position: 'absolute',
    top: 8,
    flexDirection: 'column',
    maxHeight: 220,
    overflow: 'auto',
    border: '1px solid ' + theme.palette.grey[200],
  },
  item: {
    display: 'flex',
    alignItems: 'flex-start',
    padding: theme.spacing(1.5),
    textDecoration: 'none',
    textAlign: 'left',
    transition: 'all 0.3s ease-in-out',
    color: '#9595ab',
    '&:hover': {
      backgroundColor: colors.grey200,
    },
  },
  disabled: {
    cursor: 'default',
    opacity: 0.5,
  },
  optionsList: {
    width: '100%',
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
  optionChip: {
    padding: theme.spacing(1),
    backgroundColor: colors.grey300,
  },
  control: {
    fontFamily: 'arial',
    display: 'block',
    position: 'relative',
    height: 15,
    width: 15,
    paddingLeft: 20,
    cursor: 'pointer',
    fontSize: 16,
  },
  input: {
    position: 'absolute',
    zIndex: -1,
    opacity: 0,
  },
  controlIndicator: {
    position: 'absolute',
    top: 0,
    left: 0,
    height: 15,
    width: 15,
    background: 'transparent',
    border: '1px solid ' + theme.palette.primary.main,

    '&.selected': {
      background: theme.palette.primary.main,
    },
  },
}));

function SelectedOptionsList({options}: {options: string[]}) {
  const classes = useStyles();
  return (
    <div className={classes.optionsList}>
      {options.map(o => (
        <div key={o} className={classes.optionChip}>
          {o}
        </div>
      ))}
    </div>
  );
}

export default function MultiOptionSelect(props: Props) {
  const {
    label = 'Opciones',
    onFocus,
    selectedOptions,
    setOptions,
    availableOptions,
    // minSelected,
    maxSelected,
  } = props;

  const classes = useStyles();
  const {isOutside, ref} = useOutsideAlerter();

  const [isOpen, setIsOpen] = useState<boolean>(false);

  const toggleOptions = () => {
    setIsOpen(prev => !prev);
  };

  const handleAddItem = (value: string) => {
    const alreadyExists = selectedOptions.find(o => o === value);
    const selectedOptionsCount = selectedOptions.length;
    if (
      !alreadyExists &&
      (typeof maxSelected === 'undefined' || selectedOptionsCount < maxSelected)
    ) {
      setOptions([...selectedOptions, value]);
    } else {
      setOptions(selectedOptions.filter(o => o !== value));
    }
    setIsOpen(false);
  };

  const isDisabled =
    typeof maxSelected === 'number' && selectedOptions.length >= maxSelected;

  useEffect(() => {
    if (isOutside) setIsOpen(false);
  }, [isOutside]);

  return (
    <div ref={ref} className={classes.wrap} onBlur={onFocus}>
      <div className={classes.header} onClick={toggleOptions}>
        <div>
          {selectedOptions.length > 0 ? (
            <SelectedOptionsList options={selectedOptions} />
          ) : (
            <Typography style={{padding: 8, color: '#9595ab'}}>
              {label}
            </Typography>
          )}
        </div>
        <IconButton size="small">
          {isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </div>

      <div style={{position: 'relative', bottom: 0}}>
        {isOpen && (
          <div className={classes.optionsWrap}>
            {availableOptions.map(e => {
              const isSelected = !!selectedOptions.find(el => el === e);
              return (
                <button
                  key={e}
                  className={`${classes.btn} ${classes.item} ${
                    isSelected ? classes.selected : ''
                  } ${isDisabled && !isSelected ? classes.disabled : ''}`}
                  onClick={() => handleAddItem(e)}
                >
                  <span>{e}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

MultiOptionSelect.propTypes = {
  label: PropTypes.string,
  availableOptions: PropTypes.array.isRequired,
  options: PropTypes.array.isRequired,
  setOptions: PropTypes.func.isRequired,
  minSelected: PropTypes.number,
  maxSelected: PropTypes.number,
};
